import * as React from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";

/**
 * Migrated from `@radix-ui/react-popover` to Base UI.
 *
 * 96 files consume this — 125 triggers, 124 of them `asChild`, and 123
 * contents — so no call site changes and every difference is absorbed here.
 *
 * Unlike the popups migrated before it, this file was **not pristine**. It
 * carried a local fix (#2448, "fix popover in modals") that had to survive, and
 * it turned out to be the difference that mattered most:
 *
 *  1. **`pointer-events-auto`.** A modal `Dialog` sets `pointer-events: none`
 *     on the body while open. A popover portalled out of that dialog inherits
 *     it and goes inert: visible, correctly positioned, ignoring every click.
 *     Twelve files pair a popover with a dialog. Kept, and moved onto the
 *     *positioner* as well as the popup, because under Base UI the positioner
 *     is a real element in the inheritance chain and the popup inherits `none`
 *     from it before its own rule applies.
 *  2. **The arrow changes element type.** Radix renders an `<svg>` with a
 *     `<polygon>`, which is why the caller-facing class is `fill-border`. Base
 *     UI renders a `<div>`, where `fill-*` paints nothing at all — the arrow
 *     would have silently stopped existing while still being present and
 *     sized. Base UI's `render` prop takes the original `<svg>` back, so the
 *     class keeps working and the triangle stays pixel-identical.
 *  3. **A half-migrated pair needed a temporary scroll lock.** While Dialog was
 *     still Radix, a modal Base UI popover could not re-enter Radix's
 *     `react-remove-scroll` lock, so Autocomplete lists inside a sales-order
 *     draft modal stopped scrolling (SUPP-198). #6217 mounted the lock around
 *     the popup; #6222 took it back out once Dialog moved too.
 *  4. **`Content` is two elements now.** `Positioner` owns placement, `Popup`
 *     owns the box, so `side` / `align` / `sideOffset` stop being siblings of
 *     `className`. `PopoverContent` keeps the Radix signature and splits it.
 *  5. **z-index stops propagating.** `z-[1500]` reached the screen only because
 *     Radix copied the content's computed z-index onto the wrapper it
 *     generated; the content itself is `position: static`, where z-index does
 *     nothing. It moves to the positioner — and as an inline style, not a
 *     class, because a bracketed class name on a portalled ancestor broke
 *     Handsontable's selector building in #6211.
 *  6. **The transform-origin variable is renamed and re-homed.**
 *     `--radix-popover-content-transform-origin` becomes `--transform-origin`
 *     on the positioner; custom properties inherit, so the popup still reads
 *     it. An unresolvable `transform-origin` falls back to `50% 50%` silently.
 *  7. **The state attributes.** `data-[state=open]` becomes `data-[open]`, and
 *     the closing half becomes `data-[ending-style]`.
 *
 * `PopoverAnchor` is gone. Base UI has no `Anchor` part — the positioner takes
 * an `anchor` prop instead — and the export had zero consumers, so there was
 * nothing to keep working and no shim worth inventing for nobody.
 */

/**
 * Radix defaults `collisionPadding` to `0`; Base UI defaults it to `5`. Left
 * alone, every popover that reaches a viewport edge shifts inward by five
 * pixels. Pinned to Radix's value so all 96 consumers keep their placement.
 */
const RADIX_COLLISION_PADDING = 0;

/**
 * Module scope so it is not a fresh object on every render.
 *
 * `zIndex` carries the original note — "higher than dialog z-50 and MUI
 * components to ensure proper stacking" — and is written as a style rather than
 * `z-[1500]` because Handsontable builds CSS selectors by interpolating
 * ancestor class names, and a bracketed name on a portalled ancestor made it
 * throw `SyntaxError: not a valid selector` (#6211).
 *
 * `pointerEvents` is the #2448 fix. A modal `Dialog` sets
 * `pointer-events: none` on the body; the positioner is portalled to the body
 * and inherits it, and the popup then inherits it from the positioner. Setting
 * it here restores the whole subtree in one place.
 */
const POSITIONER_STYLE: React.CSSProperties = {
  zIndex: 1500,
  pointerEvents: "auto",
};

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root {...props} />;
}

type PopoverTriggerProps = Omit<PopoverPrimitive.Trigger.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function PopoverTrigger({
  asChild = false,
  render,
  children,
  ...props
}: PopoverTriggerProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </PopoverPrimitive.Trigger>
  );
}

/**
 * Base UI renders its `Arrow` as a `<div>`, where the caller-facing
 * `fill-border` class is inert — a `fill-*` utility paints an SVG, not a box.
 * Rather than swap the class for `bg-border` and let the arrow become a
 * different shape, the same `<svg>` Radix drew is handed to Base UI through
 * `render`. That keeps `fill-*` meaningful for the two call sites that style
 * it, and keeps the rendered triangle pixel-identical.
 *
 * Base UI positions the arrow along the popup's edge but leaves the cross axis
 * to CSS, so each side anchors to its own edge and rotates the triangle to
 * point away from the popup. Radix did the same thing through a wrapper span it
 * generated; this is that span's job, expressed in classes.
 */
/**
 * The arrow SVG's own height, and it has to be added to `sideOffset` because
 * **Radix folded it in silently**: its popper offsets by
 * `sideOffset + arrowHeight`, measuring the arrow element and pushing the
 * content that much further from the trigger. Base UI does not.
 *
 * Unlike Tooltip (#6211), where the arrow is always present, `PopoverArrow` is
 * opt-in — so Radix added this only when a caller included one, and the seven
 * arrow-less baseline screenshots matched exactly while `WithArrow` sat five
 * pixels too close. The offset below is therefore conditional too.
 */
const ARROW_HEIGHT_PX = 5;

const ARROW_SVG = (
  <svg
    width="10"
    height="5"
    viewBox="0 0 30 10"
    preserveAspectRatio="none"
    style={{ display: "block" }}
  >
    <polygon points="0,0 30,0 15,10" />
  </svg>
);

/**
 * The fade is here because the arrow is a *sibling* of the popup and so does
 * not inherit its exit animation — without it the arrow stays fully opaque
 * while the box fades out. Keyed on `data-[open]`, not `data-[ending-style]`:
 * Base UI gives the arrow `data-closed` on the way out but never
 * `data-ending-style`, so the attribute the popup animates on is unavailable.
 */
const ARROW_CLASSNAME =
  "fill-border data-[side=bottom]:bottom-full data-[side=bottom]:rotate-180 data-[side=left]:left-full data-[side=left]:-rotate-90 data-[side=right]:right-full data-[side=right]:rotate-90 data-[side=top]:top-full";

type PopoverArrowProps = Omit<PopoverPrimitive.Arrow.Props, "className"> & {
  className?: string;
};

function PopoverArrow({ className, ...props }: PopoverArrowProps) {
  return (
    <PopoverPrimitive.Arrow
      data-slot="popover-arrow"
      render={ARROW_SVG}
      className={cn(ARROW_CLASSNAME, className)}
      {...props}
    />
  );
}

/**
 * Radix's placement props live on the same element as the styling; Base UI's
 * live one level up. This keeps the Radix signature and splits it.
 *
 * `children` is read out rather than spread so the arrow can be hoisted: Radix
 * nested `Arrow` inside `Content`, Base UI wants it as a sibling of `Popup`
 * under `Positioner`. Callers keep writing `<PopoverArrow />` inside
 * `<PopoverContent>` and it is re-parented here.
 */
type PopoverContentProps = Omit<
  PopoverPrimitive.Popup.Props,
  | "className"
  | "side"
  | "align"
  | "sideOffset"
  | "alignOffset"
  | "collisionBoundary"
  | "collisionPadding"
  | "anchor"
> &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | "side"
    | "align"
    | "sideOffset"
    | "alignOffset"
    | "collisionBoundary"
    | "collisionPadding"
    // What to place against instead of the trigger — Radix's `Anchor` part,
    // for a popover opened on something that isn't a React element (a grid
    // cell Handsontable renders).
    | "anchor"
  > & {
    className?: string;
    /**
     * Keep the content mounted (hidden) while closed, so state inside it — an
     * in-flight request, a retry's idempotency key — survives the popover
     * closing and reopening.
     */
    keepMounted?: boolean;
  };

function PopoverContent({
  className,
  align = "center",
  side,
  sideOffset = 4,
  alignOffset,
  collisionBoundary,
  collisionPadding = RADIX_COLLISION_PADDING,
  anchor,
  keepMounted,
  children,
  ...props
}: PopoverContentProps) {
  // Base UI wants the arrow beside the popup, not inside it. Callers write it
  // inside, as Radix required, so it is lifted out here rather than at 2 call
  // sites — and, more to the point, rather than leaving the next caller to
  // discover that the Radix spelling silently renders an arrow in the wrong
  // place.
  const { arrows, body } = React.Children.toArray(children).reduce<{
    arrows: React.ReactNode[];
    body: React.ReactNode[];
  }>(
    (split, node) => {
      const isArrow = React.isValidElement(node) && node.type === PopoverArrow;
      split[isArrow ? "arrows" : "body"].push(node);
      return split;
    },
    { arrows: [], body: [] },
  );

  // `sideOffset` keeps its Radix meaning for callers — the gap they asked for —
  // and the arrow's height is folded in here the way Radix folded it in there,
  // only when there is an arrow to account for.

  const offsetWithArrow =
    arrows.length > 0 && typeof sideOffset === "number"
      ? sideOffset + ARROW_HEIGHT_PX
      : sideOffset;

  return (
    <PopoverPrimitive.Portal keepMounted={keepMounted}>
      <PopoverPrimitive.Positioner
        data-slot="popover-positioner"
        align={align}
        side={side}
        sideOffset={offsetWithArrow}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        style={POSITIONER_STYLE}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            "bg-popover text-popover-foreground data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95 data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-72 origin-(--transform-origin) rounded-md border p-4 shadow-md outline-hidden",
            className,
          )}
          {...props}
        >
          {body}
          {/*
            Rendered *inside* the popup so it inherits everything the popup has
            — a caller's `hidden`, the exit animation, opacity. `Arrow` takes
            its position from React context rather than its DOM parent, so
            nesting it does not move it; it only restores the inheritance the
            arrow had under Radix, where it was a child of `Content`.

            Depends on the popup staying unpositioned: the arrow's `absolute`
            resolves against the positioner, so adding `relative` here would
            silently reparent that containing block.
          */}
          {arrows}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverTrigger, PopoverContent, PopoverArrow };
